/**
 * Interfaz comun de los Repositorios.
 * Patron data adapter: la UI llama Repo.X.method() sin saber donde
 * estan los datos (SharePoint, Firebase, Azure SQL, IndexedDB).
 */

export interface BaseRepo<T> {
  /** Lista todos los items del tenant actual. */
  list(filter?: Partial<T>): Promise<T[]>;

  /** Obtiene un item por id. */
  get(id: string): Promise<T | null>;

  /** Crea un item nuevo. Devuelve el id asignado. */
  create(data: Omit<T, "id" | "createdAt" | "updatedAt" | "version">): Promise<string>;

  /** Actualiza un item existente con bloqueo optimistico. */
  /**
   * Actualiza la entidad con bloqueo optimista.
   *
   * INVARIANTE — la auditoría de creación es INMUTABLE. Todas las
   * implementaciones fuerzan `createdBy` y `createdAt` desde el registro actual,
   * ignorando lo que venga en `data`.
   *
   * No es cosmético: `createdBy` es **llave de autoridad** en varias reglas de
   * alcance (`scope.ts` la consulta para PR, PO, OT y contrato marco). Los
   * formularios arman un solo payload que reutilizan para crear y para editar,
   * de modo que sin esta guarda cualquiera que abriera y guardara un registro se
   * convertía en su "creador" — y heredaba la autoridad del original.
   */
  update(id: string, data: Partial<T>, expectedVersion: number): Promise<void>;

  /** Borra un item (soft-delete recomendado). */
  delete(id: string): Promise<void>;

  /** Suscripcion a cambios (real-time si el adapter lo soporta). */
  subscribe?(callback: (items: T[]) => void): () => void;
}

/** Error de conflicto por bloqueo optimistico. */
export class OptimisticLockError extends Error {
  constructor(public entityId: string, public expectedVersion: number, public actualVersion: number) {
    super(`Optimistic lock failed for entity ${entityId}: expected v${expectedVersion}, found v${actualVersion}`);
    this.name = "OptimisticLockError";
  }
}

/** Error de validacion. */
export class ValidationError extends Error {
  constructor(message: string, public field?: string) {
    super(message);
    this.name = "ValidationError";
  }
}

/** Error de permisos. */
export class PermissionError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PermissionError";
  }
}
